import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import validate from 'validate.js';
import { Box, Button, Divider, Grid, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { InputV2, WidgetV2 } from '../../../components/v2';
import * as actionsAuth from '../../../store/auth/actions';
import * as selectorsAuth from '../../../store/auth/reducer';

const schema = {
  password: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  confirmPassword: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
    equality: 'password',
  },
};

const CRITERIA = [
  { key: 'caracters', label: 'Pelo menos 8 caracteres' },
  { key: 'maiuscula', label: 'Uma letra maiúscula (A–Z)' },
  { key: 'minuscula', label: 'Uma letra minúscula (a–z)' },
  { key: 'numero', label: 'Um número (0–9)' },
  { key: 'especial', label: 'Um caractere especial (#@?&%!$…)' },
];

const getPasswordValid = (pwd) =>
  pwd &&
  pwd.length >= 8 &&
  /[A-Z]/.test(pwd) &&
  /[a-z]/.test(pwd) &&
  /\d/.test(pwd) &&
  // eslint-disable-next-line no-useless-escape
  /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(pwd);

const shouldShowConfirmMismatch = (password = '', confirmPassword = '') => (
  password.length > 0
  && confirmPassword.length >= password.length
  && confirmPassword !== password
);

const PasswordCriterion = ({ label, valid, hasValue }) => {
  let color = 'text.disabled';
  let Icon = RadioButtonUncheckedIcon;

  if (hasValue) {
    if (valid) {
      color = 'success.main';
      Icon = CheckCircleOutlineIcon;
    } else {
      color = 'error.main';
      Icon = CancelOutlinedIcon;
    }
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Icon sx={{ fontSize: 18, color }} />
      <Typography variant="body2" sx={{ color }}>
        {label}
      </Typography>
    </Stack>
  );
};

const VisibilityToggle = ({ visible, onToggle, labelShow, labelHide }) => (
  <InputAdornment position="end">
    <IconButton
      aria-label={visible ? labelHide : labelShow}
      onClick={onToggle}
      edge="end"
      size="small"
    >
      {visible
        ? <VisibilityOutlinedIcon fontSize="small" />
        : <VisibilityOffOutlinedIcon fontSize="small" />}
    </IconButton>
  </InputAdornment>
);

const Senha = ({ updatePassword, auth }) => {
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({});
  const [viewPassword, setViewPassword] = useState(false);
  const [viewConfirmPassword, setViewConfirmPassword] = useState(false);
  const [validation, setValidation] = useState({
    caracters: false, maiuscula: false, minuscula: false, numero: false, especial: false,
  });

  const syncConfirmPasswordError = (password, confirmPassword) => {
    if (shouldShowConfirmMismatch(password, confirmPassword)) {
      setErrors((prev) => ({ ...prev, confirmPassword: true }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.confirmPassword;
        return next;
      });
    }
  };

  const handlePassword = (event) => {
    const password = event.target.value;
    setValidation({
      caracters: password && password.length >= 8,
      maiuscula: password && /[A-Z]/.test(password),
      minuscula: password && /[a-z]/.test(password),
      numero: password && /\d/.test(password),
      // eslint-disable-next-line no-useless-escape
      especial: password && /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(password),
    });
    if (!getPasswordValid(password)) {
      setErrors((prev) => ({ ...prev, password: true }));
    } else {
      setErrors((prev) => { const e = { ...prev }; delete e.password; return e; });
    }
    setValues((prev) => {
      const next = { ...prev, password };
      if (prev.confirmPassword) {
        syncConfirmPasswordError(password, prev.confirmPassword);
      }
      return next;
    });
  };

  const handleConfirmPassword = (event) => {
    const confirmPassword = event.target.value;
    setValues((prev) => {
      syncConfirmPasswordError(prev.password || '', confirmPassword);
      return { ...prev, confirmPassword };
    });
  };

  const callback = () => {
    setErrors({});
    setValues({});
    setViewPassword(false);
    setViewConfirmPassword(false);
    setValidation({ caracters: false, maiuscula: false, minuscula: false, numero: false, especial: false });
  };

  const onClick = () => {
    let erros = validate(values, schema);
    if (!getPasswordValid(values.password)) {
      erros = erros ? { ...erros, password: true } : { password: true };
    }
    setErrors(erros || {});
    if (!erros) {
      updatePassword(auth.id, values, callback, true);
    }
  };

  const hasValue = !!values.password;

  return (
    <WidgetV2 title="Alterar Senha">
      <Grid container spacing={2} sx={{ p: 2, pb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <InputV2
            error={!!(errors && errors.password)}
            hideHelperText
            label="Nova Senha"
            name="password"
            isRequired
            onChange={handlePassword}
            value={values.password || ''}
            type={viewPassword ? 'text' : 'password'}
            inputProps={{
              inputProps: { autoComplete: 'new-password' },
              endAdornment: (
                <VisibilityToggle
                  visible={viewPassword}
                  onToggle={() => setViewPassword((v) => !v)}
                  labelShow="Mostrar senha"
                  labelHide="Ocultar senha"
                />
              ),
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Critérios para a senha:
            </Typography>
            <Stack spacing={0.75}>
              {CRITERIA.map((c) => (
                <PasswordCriterion
                  key={c.key}
                  label={c.label}
                  valid={!!validation[c.key]}
                  hasValue={hasValue}
                />
              ))}
            </Stack>
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <InputV2
            error={!!(errors && errors.confirmPassword)}
            helperText={errors && errors.confirmPassword ? 'As senhas não são iguais' : null}
            showHelperText={!!(errors && errors.confirmPassword)}
            label="Confirmar Nova Senha"
            name="confirmPassword"
            isRequired
            onChange={handleConfirmPassword}
            value={values.confirmPassword || ''}
            type={viewConfirmPassword ? 'text' : 'password'}
            inputProps={{
              inputProps: { autoComplete: 'new-password' },
              endAdornment: (
                <VisibilityToggle
                  visible={viewConfirmPassword}
                  onToggle={() => setViewConfirmPassword((v) => !v)}
                  labelShow="Mostrar confirmação de senha"
                  labelHide="Ocultar confirmação de senha"
                />
              ),
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
          <Button color="primary" variant="contained" onClick={onClick} sx={{ mt: 1 }}>
            Atualizar
          </Button>
        </Grid>
      </Grid>
    </WidgetV2>
  );
};

const mapStateToProps = (state) => ({ auth: selectorsAuth.getAuth(state) });
const mapDispatchToProps = (dispatch) => bindActionCreators({ ...actionsAuth }, dispatch);
export default connect(mapStateToProps, mapDispatchToProps)(Senha);
